import { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

export default function ScatterPlot({
  data = [],
  xAccessor,
  yAccessor,
  xLabel = "",
  yLabel = "",
  referenceLines = [], // [{type: 'horizontal'|'vertical', value: number, color, dasharray}]
  tooltipRenderer,
  colorAccessor = () => "#3182ce",
  brushEnabled = true,
  plotMargin = { top: 30, right: 20, bottom: 60, left: 80 },
  transitionDuration = 1000,
  symbolAccessor = () => ({ shape: "circle", fill: true }), // NEW PROP
}) {
  const ref = useRef();
  const containerRef = useRef();
  const [plotSize, setPlotSize] = useState(400);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, d: null });
  const [brushDomain, setBrushDomain] = useState(null);
  const [showResetMessage, setShowResetMessage] = useState(false);



  useEffect(() => {
    function handleResize() {
      if (containerRef.current) {
        const size = Math.max(320, Math.min(containerRef.current.offsetWidth, 600));
        setPlotSize(size);
      }
    }
    
    // Initial resize calculation
    handleResize();
    
    // Set up window resize listener
    window.addEventListener('resize', handleResize);
    
    // Set up IntersectionObserver to detect when container becomes visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Container is now visible, recalculate size
            setTimeout(handleResize, 0); // Use setTimeout to ensure DOM is fully rendered
          }
        });
      },
      { threshold: 0.1 } // Trigger when at least 10% of the element is visible
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!data || !xAccessor || !yAccessor) return;
    const svg = d3.select(ref.current);
    // Don't remove points - let them transition
    svg.selectAll('.d3-content:not(.d3-points)').remove();
    const margin = plotMargin;
    const innerSize = plotSize - margin.left - margin.right;
    // Calculate x and y domains robustly
    const xVals = data.map(xAccessor).map(Number).filter(v => isFinite(v));
    const yVals = data.map(yAccessor).map(Number).filter(v => isFinite(v));
    const xDomain = xVals.length > 0
      ? [Math.min(0, ...xVals), Math.max(1, ...xVals)]
      : [0, 1];
    const yDomain = yVals.length > 0
      ? [Math.min(0, ...yVals), Math.max(1, ...yVals)]
      : [0, 1];

      
    // If zoomed, force both axes to same domain
    let domain = brushDomain ? [
      Math.min(brushDomain[0], brushDomain[2]),
      Math.max(brushDomain[1], brushDomain[3])
    ] : [
      Math.min(xDomain[0], yDomain[0]),
      Math.max(xDomain[1], yDomain[1])
    ];
    
    // Check if there are data points outside the current domain
    const hasPointsOutsideDomain = data.some(d => {
      const xVal = xAccessor(d);
      const yVal = yAccessor(d);
      return xVal < domain[0] || xVal > domain[1] || yVal < domain[0] || yVal > domain[1];
    });
    setShowResetMessage(brushDomain && hasPointsOutsideDomain);
    let x = d3.scaleLinear().domain(domain).range([0, innerSize]);
    let y = d3.scaleLinear().domain(domain).range([innerSize, 0]);
    const tickCount = 7;
    // Axes
    let xAxis = svg.select('.x-axis');
    if (xAxis.empty()) {
      xAxis = svg.append('g').attr('class', 'x-axis')
        .attr('transform', `translate(${margin.left},${margin.top + innerSize})`);
    } else {
      xAxis.attr('transform', `translate(${margin.left},${margin.top + innerSize})`);
    }
    xAxis.transition().duration(transitionDuration).call(d3.axisBottom(x).ticks(tickCount));
    let yAxis = svg.select('.y-axis');
    if (yAxis.empty()) {
      yAxis = svg.append('g').attr('class', 'y-axis')
        .attr('transform', `translate(${margin.left},${margin.top})`);
    } else {
      yAxis.attr('transform', `translate(${margin.left},${margin.top})`);
    }
    yAxis.transition().duration(transitionDuration).call(d3.axisLeft(y).ticks(tickCount));
    
    // Axis labels
    if (xLabel) {
      svg.selectAll('.x-label').data([0]).join('text')
        .attr('class', 'd3-content x-label')
        .attr('x', margin.left + innerSize / 2)
        .attr('y', plotSize - 10)
        .attr('text-anchor', 'middle')
        .attr('font-size', 16)
        .text(xLabel);
    }
    
    if (yLabel) {
      svg.selectAll('.y-label').data([0]).join('text')
        .attr('class', 'd3-content y-label')
        .attr('x', 18)
        .attr('y', margin.top + innerSize / 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', 16)
        .attr('transform', `rotate(-90, 18, ${margin.top + innerSize / 2})`)
        .text(yLabel);
    }
    
    // Reference lines
    referenceLines.forEach(line => {
      // Helper function to clip values to domain bounds
      const clipToDomain = (value) => {
        return Math.max(domain[0], Math.min(domain[1], value));
      };
      if (line.type === 'horizontal') {
        if (line.segment) {
          // Support xMin/xMax for horizontal segments
          let xMin = line.xMin;
          let xMax = line.xMax;
          if (xMin === undefined) xMin = domain[0];
          if (xMax === undefined) xMax = domain[1];
          if (xMin === -Infinity) xMin = domain[0];
          if (xMax === Infinity) xMax = domain[1];
          
          // Clip to domain bounds
          xMin = clipToDomain(xMin);
          xMax = clipToDomain(xMax);
          const yVal = y(line.value) + margin.top;
          svg.append('line')
            .attr('class', 'd3-content')
            .attr('x1', x(xMin) + margin.left)
            .attr('y1', yVal)
            .attr('x2', x(xMax) + margin.left)
            .attr('y2', yVal)
            .attr('stroke', line.color || '#888')
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', line.dasharray || '6,4');
        } else {
          // Clip y value to domain bounds
          const clippedValue = clipToDomain(line.value);
          const yVal = y(clippedValue);
          const safeMarginTop = isFinite(margin?.top) ? margin.top : 0;
          if (isFinite(yVal)) {
            svg.append('line')
              .attr('class', 'd3-content')
              .attr('x1', x.range()[0] + margin.left)
              .attr('y1', yVal + safeMarginTop)
              .attr('x2', x.range()[1] + margin.left)
              .attr('y2', yVal + safeMarginTop)
              .attr('stroke', line.color || '#888')
              .attr('stroke-width', 1.5)
              .attr('stroke-dasharray', line.dasharray || '6,4');
          }
        }
      } else if (line.type === 'vertical') {
        if (line.segment) {
          // Support yMin/yMax for vertical segments
          let yMin = isFinite(line.yMin) ? line.yMin : domain[0];
          let yMax = isFinite(line.yMax) ? line.yMax : domain[1];
          if (yMin === -Infinity) yMin = domain[0];
          if (yMax === Infinity) yMax = domain[1];
          
          // Clip to domain bounds
          yMin = clipToDomain(yMin);
          yMax = clipToDomain(yMax);
          const xVal = x(line.value);
          const safeMarginTop = isFinite(margin?.top) ? margin.top : 0;
          const y1 = y(yMin) + safeMarginTop;
          const y2 = y(yMax) + safeMarginTop;
          if (isFinite(xVal) && isFinite(y1) && isFinite(y2)) {
            svg.append('line')
              .attr('class', 'd3-content')
              .attr('x1', xVal + margin.left)
              .attr('y1', y1)
              .attr('x2', xVal + margin.left)
              .attr('y2', y2)
              .attr('stroke', line.color || '#888')
              .attr('stroke-width', 1.5)
              .attr('stroke-dasharray', line.dasharray || '6,4');
          }
        } else {
          // Clip x value to domain bounds
          const clippedValue = clipToDomain(line.value);
          svg.append('line')
            .attr('class', 'd3-content')
            .attr('x1', x(clippedValue) + margin.left)
            .attr('y1', y.range()[0] + margin.top)
            .attr('x2', x(clippedValue) + margin.left)
            .attr('y2', y.range()[1] + margin.top)
            .attr('stroke', line.color || '#888')
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', line.dasharray || '6,4');
        }
      } else if (line.type === 'diagonal') {
        if (line.segment && line.x0 != null && line.y0 != null && line.x1 != null && line.y1 != null) {
          // Clip coordinates to domain bounds
          const x0 = clipToDomain(line.x0);
          const y0 = clipToDomain(line.y0);
          const x1 = clipToDomain(line.x1);
          const y1 = clipToDomain(line.y1);
          
          svg.append('line')
            .attr('class', 'd3-content')
            .attr('x1', x(x0) + margin.left)
            .attr('y1', y(y0) + margin.top)
            .attr('x2', x(x1) + margin.left)
            .attr('y2', y(y1) + margin.top)
            .attr('stroke', line.color || '#888')
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', line.dasharray || '6,4');
        } else {
          svg.append('line')
            .attr('class', 'd3-content')
            .attr('x1', x.range()[0] + margin.left)
            .attr('y1', y(domain[0]) + margin.top)
            .attr('x2', x.range()[1] + margin.left)
            .attr('y2', y(domain[1]) + margin.top)
            .attr('stroke', line.color || '#888')
            .attr('stroke-width', 1.5)
            .attr('stroke-dasharray', line.dasharray || '6,4');
        }
      }
    });
    // Points
    let pointsGroup = svg.select('.d3-points');
    if (pointsGroup.empty()) {
      pointsGroup = svg.append('g')
        .attr('class', 'd3-content d3-points')
        .attr('transform', `translate(${margin.left},${margin.top})`);
    } else {
      pointsGroup.attr('transform', `translate(${margin.left},${margin.top})`);
    }
    const points = pointsGroup.selectAll('circle').data(data, d => d.id || d.index || JSON.stringify(d));
    points.join(
      enter => enter.append('circle')
        .attr('cx', d => x(xAccessor(d)))
        .attr('cy', d => y(yAccessor(d)))
        .attr('r', 5)
        .attr('fill', d => {
          const symbol = symbolAccessor(d);
          return symbol.fill ? colorAccessor(d) : "white";
        })
        .attr('stroke', d => {
          const symbol = symbolAccessor(d);
          return symbol.fill ? "none" : colorAccessor(d);
        })
        .attr('stroke-width', d => {
          const symbol = symbolAccessor(d);
          return symbol.fill ? 0 : 2;
        })
        .attr('opacity', 0)
        .style('cursor', 'pointer')
        .on('mouseover', function (event, d) {
          const [mx, my] = d3.pointer(event, this.ownerSVGElement);
          setTooltip({ visible: true, x: mx, y: my, d });
        })
        .on('mousemove', function (event, d) {
          const [mx, my] = d3.pointer(event, this.ownerSVGElement);
          setTooltip(t => ({ ...t, x: mx, y: my }));
        })
        .on('mouseout', function () {
          setTooltip({ visible: false, x: 0, y: 0, d: null });
        })
        .transition().duration(500)
        .attr('opacity', 1),
      update => update.transition().duration(1000)
        .attr('cx', d => x(xAccessor(d)))
        .attr('cy', d => y(yAccessor(d)))
        .attr('fill', d => {
          const symbol = symbolAccessor(d);
          return symbol.fill ? colorAccessor(d) : "white";
        })
        .attr('stroke', d => {
          const symbol = symbolAccessor(d);
          return symbol.fill ? "none" : colorAccessor(d);
        })
        .attr('stroke-width', d => {
          const symbol = symbolAccessor(d);
          return symbol.fill ? 0 : 2;
        })
        .attr('opacity', 1),
      exit => exit.transition().duration(500)
        .attr('opacity', 0)
        .remove()
    );
    // Brush
    svg.selectAll('.brush').remove();
    if (brushEnabled) {
      const brush = d3.brush()
        .extent([[margin.left, margin.top], [margin.left + innerSize, margin.top + innerSize]])
        .on('start', () => {
          // Add transparent overlay to prevent tooltips during brushing
          const overlay = document.createElement('div');
          overlay.style.position = 'absolute';
          overlay.style.top = '0';
          overlay.style.left = '0';
          overlay.style.width = '100%';
          overlay.style.height = '100%';
          overlay.style.zIndex = '1000';
          overlay.style.cursor = 'crosshair';
          overlay.id = 'brush-overlay';
          containerRef.current.appendChild(overlay);
          
          // Add double-click handler to overlay for reset
          overlay.addEventListener('dblclick', () => setBrushDomain(null));
        })
        .on('end', (event) => {
          // Remove overlay when brushing ends
          const existing = document.getElementById('brush-overlay');
          if (existing) {
            existing.remove();
          }
          
          if (!event.selection) return;
          const [[x0, y0], [x1, y1]] = event.selection;
          // Convert pixel selection to data domain
          const xDomain = [x.invert(x0 - margin.left), x.invert(x1 - margin.left)];
          const yDomain = [y.invert(y1 - margin.top), y.invert(y0 - margin.top)];
          setBrushDomain([xDomain[0], xDomain[1], yDomain[0], yDomain[1]]);
          svg.select('.brush').call(brush.move, null);
        });
      svg.insert('g', ':first-child')
        .attr('class', 'brush')
        .call(brush);
      svg.on('dblclick', () => setBrushDomain(null));
    }
  }, [data, plotSize, brushDomain, plotMargin, xAccessor, yAccessor, colorAccessor, referenceLines, brushEnabled, transitionDuration, symbolAccessor]);

  return (
    <div ref={containerRef} className="w-full min-w-[320px] relative">
      <svg ref={ref} width="100%" height={plotSize} viewBox={`0 0 ${plotSize} ${plotSize}`} preserveAspectRatio="none" />
      {tooltip.visible && tooltip.d && tooltipRenderer && tooltipRenderer(tooltip)}
      {showResetMessage && (
        <div className="text-center text-base text-gray-600 mt-2">
          💡 Some data points are outside the view.{" "}
          <button
            onClick={() => setBrushDomain(null)}
            className="text-blue-600 underline hover:text-blue-800 transition-colors cursor-pointer"
          >
            Click here to reset zoom
          </button>
        </div>
      )}
    </div>
  );
} 